import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useCatalogSettings, useUpdateCatalogSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import {
  ChipListField,
  FeedbackSnackbar,
  SettingsCard,
  SettingsScaffold,
  TextInputField,
} from '../components';

/** Storefront catalog behaviour. */
export function CatalogSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('catalog');
  const query = useCatalogSettings();
  const update = useUpdateCatalogSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('catalogSettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('catalogSettings.title')}
      subtitle={t('catalogSettings.subtitle')}
      icon={<Inventory2RoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('catalogSettings.visibilitySection.title')} icon={<Inventory2RoundedIcon />}>
              <TextInputField
                label={t('catalogSettings.visibilitySection.productPublicationRuleLabel')}
                value={draft.productPublicationRule}
                onChange={(value) => form.setField('productPublicationRule', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('catalogSettings.visibilitySection.categoryVisibilityRuleLabel')}
                value={draft.categoryVisibilityRule}
                onChange={(value) => form.setField('categoryVisibilityRule', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('catalogSettings.visibilitySection.attributeDisplayRuleLabel')}
                value={draft.attributeDisplayRule}
                onChange={(value) => form.setField('attributeDisplayRule', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('catalogSettings.visibilitySection.availabilityDisplayLabel')}
                value={draft.availabilityDisplay}
                onChange={(value) => form.setField('availabilityDisplay', value)}
                disabled={disabled}
              />
              <ChipListField
                label={t('catalogSettings.visibilitySection.mediaRulesLabel')}
                description={t('catalogSettings.visibilitySection.mediaRulesDescription')}
                value={draft.mediaRules}
                onChange={(value) => form.setField('mediaRules', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title={t('catalogSettings.searchSection.title')} icon={<ManageSearchRoundedIcon />}>
              <TextInputField
                label={t('catalogSettings.searchSection.searchBehaviorLabel')}
                value={draft.searchBehavior}
                onChange={(value) => form.setField('searchBehavior', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard
              title={t('catalogSettings.seoSection.title')}
              description={t('catalogSettings.seoSection.description')}
              icon={<TravelExploreRoundedIcon />}
            >
              <TextInputField
                label={t('catalogSettings.seoSection.titleTemplateLabel')}
                value={draft.seoDefaults.titleTemplate ?? ''}
                onChange={(value) =>
                  form.setField('seoDefaults', {
                    ...draft.seoDefaults,
                    titleTemplate: value || null,
                  })
                }
                disabled={disabled}
              />
              <TextInputField
                label={t('catalogSettings.seoSection.descriptionTemplateLabel')}
                value={draft.seoDefaults.descriptionTemplate ?? ''}
                onChange={(value) =>
                  form.setField('seoDefaults', {
                    ...draft.seoDefaults,
                    descriptionTemplate: value || null,
                  })
                }
                disabled={disabled}
                multiline
                rows={2}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
